<template>
  <div class="relative h-full">
    <OrCardV3
      class="!p-0 h-full min-h-[288px]"
      data-test="card"
      @click="openLatestDocs"
    >
      <div class="card-content flex h-full min-h-[288px] flex-col p-md">
        <div
          class="flex min-h-0 grow flex-col"
        >
          <div>
            <div
              class="flex h-[48px] w-[48px] shrink-0 items-center justify-center overflow-hidden rounded-sm text-white typography-headline-3"
              :style="{ background: previewIconBackground }"
            >
              <img
                v-if="isPreviewVisible"
                class="h-full w-full object-cover"
                :src="preview"
                :alt="label"
                @error="onPreviewError"
              >
              <span v-else>{{ initials }}</span>
            </div>

            <span
              class="mt-md block truncate typography-headline-3"
              data-test="project-name"
            >{{ label }}</span>
            <p
              v-if="description"
              class="mt-xs overflow-hidden text-on-surface dark:text-on-surface-dark typography-body-2-regular [display:-webkit-box] [-webkit-line-clamp:3] [-webkit-box-orient:vertical]"
              data-test="project-description"
            >
              {{ description }}
            </p>
          </div>
          <div class="mt-auto min-h-[24px] pt-md">
            <OrTagsV3
              v-if="visibleTags.length"
              :items="visibleTags"
              overflow="hidden"
              :tag-props="{ color: 'default' }"
              data-test="project-tags"
            />
          </div>
        </div>
        <div
          class="mt-md"
          @click.stop
        >
          <VersionSelector
            :version="latestVersionLabel"
            :versions="popoverVersions"
            :latest-version="latestVersionLabel"
            :is-loading="list == null"
            :project-name="projectName"
            @open="ensureVersionsLoaded"
            @versionDelete="emit('versionDelete', {version: $event, project: projectName})"
          />
        </div>
      </div>
    </OrCardV3>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { generateColorFromString, getAbbreviation, OrCardV3, OrTagsV3 } from '@onereach/ui-components';
import useProjectVersionStore from '@/stores/Version.store';
import VersionSelector from './VersionSelector.vue';
import * as semver from 'semver';
import { getDocsUrl } from '@/utils/docsUrl';

type ProjectTag = {
  label: string;
  value: string;
  tagProps?: {
    color: 'default' | 'danger';
    class?: string;
  };
};

const props = withDefaults(defineProps<{
  docsUrl: string;
  projectName: string;
  label: string;
  preview: string;
  description?: string;
  tags?: string[];
  latestVersion?: string | null;
}>(), {
  description: '',
  tags: () => [],
  latestVersion: null,
});

const emit = defineEmits<{
  (event: 'versionDelete', payload: { version: string; project: string; }): void;
}>();

const { projectVersions, getProjectVersions } = useProjectVersionStore();
const isPreviewVisible = ref(true);

const list = computed(() => projectVersions(props.projectName));
const latest = computed(() => getLatestVersion(list.value || []));
const popoverVersions = computed(() => (list.value || []).filter(version => version !== latestVersionLabel.value));
const latestVersionLabel = computed(() => props.latestVersion || latest.value || '');
const latestDocsUrl = computed(() => props.docsUrl || (latestVersionLabel.value ? getDocsUrl(props.projectName, latestVersionLabel.value) : ''));
const visibleTags = computed<ProjectTag[]>(() => props.tags.map(tag => ({
  label: tag,
  value: tag,
  tagProps: getTagProps(tag),
})));
const initials = computed(() => getAbbreviation(props.label, 2));
const previewIconBackground = computed(() => generateColorFromString(props.label));

onMounted(() => {
  if (!props.latestVersion) {
    ensureVersionsLoaded();
  }
});

function onPreviewError() {
  isPreviewVisible.value = false;
}

function getTagProps(tag: string): ProjectTag['tagProps'] {
  const normalizedTag = tag.trim().toLowerCase();
  if (normalizedTag === 'new') {
    return {
      color: 'default',
      class: '!bg-success-opacity-0-08 !text-success dark:!bg-success-opacity-0-08-dark dark:!text-success-dark',
    };
  }
  if (['deprecated', 'depricated'].includes(normalizedTag)) {
    return {
      color: 'danger',
    };
  }
  return {
    color: 'default',
  };
}

async function openLatestDocs(event: MouseEvent) {
  if (latestDocsUrl.value) {
    window.open(latestDocsUrl.value, '_blank', 'noopener,noreferrer');
    return;
  }

  event.preventDefault();
  const target = window.open('', '_blank');

  if (target) {
    target.opener = null;
  }

  await ensureVersionsLoaded();

  const latestResolved = getLatestVersion(list.value || []);
  const url = latestResolved ? getDocsUrl(props.projectName, latestResolved) : '';

  if (target && url) {
    target.location.href = url;
  } else if (target) {
    target.close();
  }
}

async function ensureVersionsLoaded() {
  if (list.value == null) {
    await getProjectVersions(props.projectName);
  }
}

function getLatestVersion(versions: string[]) {
  return semver.maxSatisfying<string>(versions, '*', { includePrerelease: true });
}
</script>
